import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, useLocation, NavLink, useNavigate, Navigate } from "react-router-dom";
import { doc, onSnapshot, collection, getDocs, getDoc, query, where } from "firebase/firestore";
import { db } from "./utils/firebase";
import ProjectNav from "./components/ProjectNav";
import ContentList from "./components/ContentList";
import Editor from "./components/Editor";
import TipEditor from "./components/TipEditor";
import GeneralSettings from "./components/GeneralSettings";
import AppSettings from "./components/AppSettings";
import PremiumFiles from "./components/PremiumFiles";
import { useAuth } from "../../context/AuthContext";
import { logActivity } from "./activityLog";
import { fnUrl } from "../../utils/fnUrl";
import { toast } from "react-toastify";
import "../../styles/cms.css";

const CONTENT_TYPES = [
  { id: "insights", label: "Insights" },
  { id: "recipes", label: "Recipes" },
  { id: "mealplans", label: "Meal Plans" },
  { id: "workouts", label: "Workouts" },
  { id: "tips", label: "Tips" },
  { id: "premium", label: "Premium" },
  { id: "app-settings", label: "App Settings" },
];

function findFirstText(blocks: any[], type: string, locale: string): string {
  if (!Array.isArray(blocks)) return "";
  for (const b of blocks) {
    if (b.type === type) {
      const val = b.text;
      if (val && typeof val === "object" && !Array.isArray(val)) {
        return val[locale] || val.en || "";
      }
      return typeof val === "string" ? val : "";
    }
    if (Array.isArray(b.content)) {
      const found = findFirstText(b.content, type, locale);
      if (found) return found;
    }
  }
  return "";
}

// ─── Deploy Dialog ──────────────────────────────────────────────────────────

interface NewInsight {
  id: string;
  title: string;
  selected: boolean;
}

interface DeployDialogProps {
  open: boolean;
  env: "dev" | "prod";
  project: string;
  onClose: () => void;
  onDeploy: (env: "dev" | "prod", push: { enabled: boolean; insightIds: string[]; scheduleTime: string | null }) => void;
}

const DeployDialog: React.FC<DeployDialogProps> = ({ open, env, project, onClose, onDeploy }) => {
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushTime, setPushTime] = useState("");
  const [newInsights, setNewInsights] = useState<NewInsight[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !project) return;
    setLoading(true);

    const load = async () => {
      try {
        // Load new insights (meta.isNew === true)
        const q = query(
          collection(db, "content", project, "insights"),
          where("meta.isNew", "==", true)
        );
        const snap = await getDocs(q);
        const insights: NewInsight[] = [];
        snap.forEach((d) => {
          const data = d.data();
          const blocks = data.data?.content || data.content || [];
          const title = findFirstText(blocks, "title", "en") || d.id;
          insights.push({ id: d.id, title, selected: true });
        });
        setNewInsights(insights);

        // Load push config defaults
        const configSnap = await getDoc(doc(db, "content", project, "_config", "push"));
        if (configSnap.exists()) {
          const cfg = configSnap.data();
          setPushEnabled(cfg.autoPush?.enabled ?? false);
          setPushTime(cfg.autoPush?.scheduleTime ?? "");
        } else {
          setPushEnabled(false);
          setPushTime("");
        }
      } catch (err) {
        console.error("[DeployDialog] Load error:", err);
      }
      setLoading(false);
    };
    load();
  }, [open, project]);

  if (!open) return null;

  const envLabel = env === "prod" ? "Production" : "Stage";
  const hasInsights = newInsights.length > 0;
  const selectedIds = newInsights.filter((i) => i.selected).map((i) => i.id);

  const toggleInsight = (id: string) => {
    setNewInsights((prev) =>
      prev.map((i) => (i.id === id ? { ...i, selected: !i.selected } : i))
    );
  };

  const handleDeploy = () => {
    onDeploy(env, {
      enabled: pushEnabled && selectedIds.length > 0,
      insightIds: pushEnabled ? selectedIds : [],
      scheduleTime: pushEnabled && pushTime ? pushTime : null,
    });
  };

  return (
    <div className="deploy-dialog__overlay" onClick={onClose}>
      <div className="deploy-dialog" onClick={(e) => e.stopPropagation()}>
        <h3 className="deploy-dialog__title">Deploy to {envLabel}</h3>
        <p className="deploy-dialog__desc">
          Deploy <strong>{project}</strong> content to {envLabel.toLowerCase()}.
        </p>

        {loading ? (
          <div className="deploy-dialog__loading">Loading...</div>
        ) : (
          <>
            <label className="deploy-dialog__toggle">
              <input
                type="checkbox"
                checked={pushEnabled}
                onChange={(e) => setPushEnabled(e.target.checked)}
                disabled={!hasInsights}
              />
              <span>Send push notifications</span>
            </label>

            {pushEnabled && hasInsights && (
              <>
                <div className="deploy-dialog__section-label">Select insights:</div>
                <div className="deploy-dialog__insights">
                  {newInsights.map((insight) => (
                    <label key={insight.id} className="deploy-dialog__insight">
                      <input
                        type="checkbox"
                        checked={insight.selected}
                        onChange={() => toggleInsight(insight.id)}
                      />
                      <span>{insight.title}</span>
                    </label>
                  ))}
                </div>

                <div className="deploy-dialog__section-label">Schedule time (optional):</div>
                <input
                  type="time"
                  className="deploy-dialog__time"
                  value={pushTime}
                  onChange={(e) => setPushTime(e.target.value)}
                  placeholder="Leave empty for immediate"
                />
              </>
            )}

            {!hasInsights && (
              <div className="deploy-dialog__empty">No new insights to push.</div>
            )}
          </>
        )}

        <div className="deploy-dialog__actions">
          <button className="deploy-dialog__cancel" onClick={onClose}>Cancel</button>
          <button
            className={`deploy-dialog__deploy${env === "prod" ? " deploy-dialog__deploy--prod" : ""}`}
            onClick={handleDeploy}
            disabled={loading}
          >
            Deploy
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Content Type Tabs ──────────────────────────────────────────────────────

const ContentTypeTabs = () => {
  const location = useLocation();
  const { isDev, user, firebaseUser } = useAuth();
  const pathParts = location.pathname.replace(/^\/cms\/?/, "").split("/");
  const project = pathParts[0] || null;
  const [deploying, setDeploying] = useState(false);
  const [showEnvMenu, setShowEnvMenu] = useState(false);
  const [deployDialog, setDeployDialog] = useState<{ open: boolean; env: "dev" | "prod" }>({ open: false, env: "dev" });
  const menuRef = useRef<HTMLDivElement>(null);
  const mountedAtRef = useRef<Date>(new Date());

  useEffect(() => {
    if (!project) return;
    const unsub = onSnapshot(doc(db, "content", project, "_config", "deploy_status"), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      const completedAt: Date | null = data.completedAt?.toDate?.() || null;
      if (!completedAt || completedAt <= mountedAtRef.current) return;
      const { status, env } = data;
      const envLabel = env === "prod" ? "Production" : "Stage";
      if (status === "success") {
        toast.success(`${envLabel} deployed!`);
        logActivity(project, { action: "deploy_complete", detail: env, user: "ci" });
      } else {
        toast.error(`${envLabel} deploy failed`);
      }
    });
    return () => unsub();
  }, [project]);

  if (!project) return null;

  const openDeployDialog = (env: "dev" | "prod") => {
    setShowEnvMenu(false);
    if (deploying) return;
    setDeployDialog({ open: true, env });
  };

  const handleDeploy = async (env: "dev" | "prod", push: { enabled: boolean; insightIds: string[]; scheduleTime: string | null }) => {
    setDeployDialog({ open: false, env: "dev" });
    setDeploying(true);
    try {
      const base = fnUrl("cmsDeploy");
      const body: any = { project, env, user: user?.email || "unknown" };
      if (push.enabled && push.insightIds.length > 0) {
        body.push = {
          enabled: true,
          insightIds: push.insightIds,
          scheduleTime: push.scheduleTime,
        };
      }
      const resp = await fetch(base, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.error || `HTTP ${resp.status}`);
      const pushLabel = push.enabled && push.insightIds.length > 0
        ? ` + ${push.insightIds.length} push${push.insightIds.length > 1 ? "es" : ""}`
        : "";
      toast.success(`Deploy started (${env})${pushLabel}`);
      logActivity(project, { action: "deploy", detail: env, user: user?.email || firebaseUser?.email || "unknown", photoURL: user?.photoURL || firebaseUser?.photoURL || undefined }).catch(() => {});
    } catch (err: any) {
      console.error("[CmsPage] Deploy error:", err);
      toast.error(`Deploy failed: ${err.message}`);
    } finally {
      setDeploying(false);
    }
  };

  return (
    <div className="cms__tabs">
      {CONTENT_TYPES.map((t) => (
        <NavLink
          key={t.id}
          to={`/cms/${project}/${t.id}`}
          className={({ isActive }) =>
            `cms__tab ${isActive ? "cms__tab--active" : ""}`
          }
        >
          {t.label}
        </NavLink>
      ))}
      {isDev && (
        <NavLink
          to={`/cms/${project}/general`}
          className={({ isActive }) =>
            `cms__tab ${isActive ? "cms__tab--active" : ""}`
          }
        >
          General
        </NavLink>
      )}
      <div className="cms__deploy-wrap" ref={menuRef}>
        <button
          className="cms__deploy-btn"
          onClick={() => setShowEnvMenu((v) => !v)}
          disabled={deploying}
        >
          {deploying ? "Deploying..." : "Deploy ▾"}
        </button>
        {showEnvMenu && (
          <div className="cms__deploy-menu">
            <button onClick={() => openDeployDialog("dev")}>Stage</button>
            <button className="cms__deploy-menu--prod" onClick={() => openDeployDialog("prod")}>Prod</button>
          </div>
        )}
      </div>

      <DeployDialog
        open={deployDialog.open}
        env={deployDialog.env}
        project={project}
        onClose={() => setDeployDialog({ open: false, env: "dev" })}
        onDeploy={handleDeploy}
      />
    </div>
  );
};

const CmsPage = () => {
  const { isDev } = useAuth();
  return (
    <div className="cms">
      <ProjectNav />
      <div className="cms__main">
        <ContentTypeTabs />
        <Routes>
          <Route index element={<CmsRedirect />} />
          <Route path=":project" element={<CmsProjectWelcome />} />
          <Route path=":project/general" element={isDev ? <GeneralSettings /> : <CmsProjectWelcome />} />
          <Route path=":project/premium" element={<PremiumFiles />} />
          <Route path=":project/app-settings" element={<AppSettings />} />
          <Route path=":project/tips" element={<TipEditor />} />
          <Route path=":project/:contentType/categories" element={<ContentList />} />
          <Route path=":project/:contentType" element={<ContentList />} />
          <Route path=":project/:contentType/:docId" element={<Editor />} />
        </Routes>
      </div>
    </div>
  );
};

const CmsRedirect = () => {
  const navigate = useNavigate();
  const { hasProject } = useAuth();
  const [done, setDone] = useState(false);

  useEffect(() => {
    const go = async () => {
      try {
        const snap = await getDocs(collection(db, "content"));
        const projects: { id: string; name: string }[] = [];
        snap.forEach((doc) => {
          const d = doc.data();
          if (d.name && hasProject(doc.id)) projects.push({ id: doc.id, name: d.name });
        });
        projects.sort((a, b) => a.name.localeCompare(b.name));
        if (projects.length) {
          navigate(`/cms/${projects[0].id}/insights`, { replace: true });
          return;
        }
      } catch (err) {
        console.error("[CmsRedirect]", err);
      }
      setDone(true);
    };
    go();
  }, []);

  if (done) return <div className="cms__welcome"><p>No projects available.</p></div>;
  return <div className="cms__loading">Loading...</div>;
};

const CmsProjectWelcome = () => (
  <div className="cms__welcome">
    <p>Select a content type above.</p>
  </div>
);

export default CmsPage;
